import { useDebouncedCallback } from "use-debounce";
import { useImperativeHandle, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Table, TableInstance } from "@monolith-forensics/monolith-ui";
import styled from "styled-components";

import { isConnectionFailure } from "@/api/connection.types";
import UserApi from "@/api/users/users";
import { useClearTransientTableState, useTableState } from "@/Features/_shared";
import { UsersAPI } from "@/Features/Users/users.query";
import type { User } from "@/Features/Users/users.types";
import { AssetValidationsAPI } from "../asset-validations.api";
import { assetValidationQueryKeys } from "../asset-validations.query";
import { AssetValidationFlyout } from "../Flyout/AssetValidationFlyout";
import type {
  AssetValidation,
  AssetValidationSortableColumn,
  QueryAssetValidationsParams,
} from "../asset-validations.types";
import {
  AssetValidationColumnDefinitions,
  AssetValidationRow,
  UsersById,
  withUserNames,
} from "./AssetValidationColumnDefinitions";

const DEFAULT_PAGE_SIZE = 100;

// VALIDATION_SORTABLE_COLUMNS on the api. Every column with enableSorting in
// the column defs must be here, or a header click on it silently falls back
// to the default sort; the rest guards a persisted sortState from an older
// column set, which the api would 400 on.
const SORTABLE_COLUMNS: readonly string[] = [
  "id",
  "validation_date",
  "next_validation_due",
  "version",
  "created_by_id",
  "completed_by_id",
];

const isSortableColumn = (
  field: string | undefined
): field is AssetValidationSortableColumn =>
  !!field && SORTABLE_COLUMNS.includes(field);

// The users list only changes when an admin edits users; 5 minutes matches
// the asset meta cache so both expire together.
const USERS_STALE_TIME = 5 * 60 * 1000;

// Same shell as LabAssetsSection's StyledTableWrap: the virtualized table
// needs a bounded flex parent or it renders at zero height inside the
// details page's tab body.
const TableWrap = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
`;

// Deliberately just { refresh } (see LabAssetsTableHandle).
export type AssetValidationsTableHandle = {
  refresh: () => void;
};

/**
 * One asset's validations (ENG-1672): GET /validations?asset_uuid=…, infinite
 * scroll, table-menu search, column sort/reorder/visibility persisted through
 * stateStorage. `onCreateClick` opens the ENG-999 create modal; the row
 * action button opens AssetValidationFlyout (ENG-1000) unless a host passes
 * `onActionButtonClick` to take over. Mutations invalidate
 * `assetValidationQueryKeys.infiniteLists` or call `tableRef.current.refresh()`.
 */
export const AssetValidationsTable: React.FC<{
  assetUuid: string;
  tableRef?: React.MutableRefObject<AssetValidationsTableHandle | undefined>;
  stateStoreKey?: string;
  onCreateClick?: () => void;
  onActionButtonClick?: (rowData: AssetValidation) => void;
}> = ({
  assetUuid,
  tableRef,
  stateStoreKey = "asset-validations:table",
  onCreateClick,
  onActionButtonClick,
}) => {
  const tableInstanceRef = useRef<TableInstance>();
  const [flyoutData, setFlyoutData] = useState<AssetValidationRow | null>(null);
  const [showFlyout, setShowFlyout] = useState(false);

  // The key is shared across assets so the column layout follows the user;
  // strip the persisted search first so a term typed on one asset never
  // filters another asset's table.
  useClearTransientTableState(stateStoreKey);
  const tableState = useTableState<QueryAssetValidationsParams>(
    { asset_uuid: assetUuid },
    stateStoreKey,
    DEFAULT_PAGE_SIZE
  );
  const resolvedQuery = tableState.resolvedQuery;

  const {
    data,
    isLoading,
    isFetched,
    isFetchingNextPage,
    hasNextPage,
    refetch,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: assetValidationQueryKeys.infiniteList(resolvedQuery),
    // listValidationsQuerySchema is .strict() (400 on unknown keys), so map
    // the table-state shape to the query params explicitly; no `filter`
    // since the table exposes no filter rules.
    queryFn: ({ pageParam }) => {
      const sortBy = isSortableColumn(resolvedQuery.sort_by)
        ? resolvedQuery.sort_by
        : undefined;
      return AssetValidationsAPI.get({
        asset_uuid: assetUuid,
        page: pageParam as number,
        page_size: DEFAULT_PAGE_SIZE,
        // Trimmed here as well as in onSearch: mfui persists the raw input
        // and useTableState restores it untouched.
        search: resolvedQuery.search?.trim() || undefined,
        // Latest validation on top when the table carries no sort; the api
        // defaults to id asc.
        sort_by: sortBy ?? "validation_date",
        sort_dir: sortBy ? resolvedQuery.sort_dir : "desc",
      });
    },
    getNextPageParam: (lastPage) => lastPage.next_page,
    initialPageParam: 1,
    placeholderData: (previous) => previous,
    enabled: !!assetUuid,
  });

  // Rows carry user ids only; resolve names from the shared users list.
  // Inactive and observer users are included so a validation completed by a
  // since-deactivated user still shows a name. A failed fetch (interceptor
  // already toasted) leaves the map empty and the cells blank. Fetched with
  // data_only: false and thrown on failure: the default path resolves `[]`
  // on a connection failure, which react-query would cache as success for
  // USERS_STALE_TIME instead of refetching on the next mount.
  const { data: users } = useQuery({
    queryKey: UsersAPI.queryKeys.list({}),
    queryFn: async (): Promise<User[]> => {
      const res = await UserApi.getUsers({
        includeInactive: true,
        includeObservers: true,
        data_only: false,
      });
      if (isConnectionFailure(res)) throw new Error("users fetch failed");
      return res?.data ?? [];
    },
    staleTime: USERS_STALE_TIME,
    retry: false,
  });

  const usersById = useMemo<UsersById>(
    () =>
      new Map(
        (Array.isArray(users) ? users : []).map((u) => [u.user_id, u.full_name])
      ),
    [users]
  );

  const records = useMemo(
    () =>
      withUserNames(
        // connection.js resolves failed requests with { success: false }, so
        // page.data can be undefined on an api error.
        data?.pages?.reduce<AssetValidation[]>(
          (acc, page) => [...acc, ...(page.data ?? [])],
          []
        ) || [],
        usersById
      ),
    [data?.pages, usersById]
  );

  // Guard against repeated scroll-to-bottom events restarting the in-flight
  // page request (see LabAssetsTable).
  const debouncedFetchNextPage = useDebouncedCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, 50);

  const handleActionButtonClick = (rowData: AssetValidationRow) => {
    if (onActionButtonClick) {
      onActionButtonClick(rowData);
      return;
    }
    setFlyoutData(rowData);
    setShowFlyout(true);
  };

  // Flyout prev/next walk the loaded rows and wrap at the ends (same as
  // LabAssetsTable).
  const handleGetNextItem = (uuid: string) => {
    if (!uuid || records.length === 0) return;
    const current = records.findIndex((i) => i.uuid === uuid);
    if (current === -1) return;
    setFlyoutData(records[current + 1] || records[0]);
  };

  const handleGetPrevItem = (uuid: string) => {
    if (!uuid || records.length === 0) return;
    const current = records.findIndex((i) => i.uuid === uuid);
    if (current === -1) return;
    setFlyoutData(records[current - 1] || records[records.length - 1]);
  };

  useImperativeHandle(
    tableRef,
    () => ({
      refresh: () => {
        refetch();
      },
    }),
    [refetch]
  );

  const loading = isLoading || !isFetched || isFetchingNextPage;

  return (
    <TableWrap>
      <Table
        data={records}
        columnProps={AssetValidationColumnDefinitions}
        keyField="uuid"
        virtualized={true}
        tableInstanceRef={tableInstanceRef}
        totalRecords={data?.pages?.[0]?.total || 0}
        loading={loading}
        // Highlights the row the open flyout shows (follows prev/next) and
        // clears when it closes, so a closed or deleted row isn't left lit.
        focusedRowId={showFlyout ? flyoutData?.uuid : undefined}
        stateStorage={{
          enabled: !!stateStoreKey,
          type: "localStorage",
          key: stateStoreKey || "",
        }}
        onScrollToBottom={debouncedFetchNextPage}
        // A click on a column without enableSorting (Description) arrives
        // with sort undefined and mfui has already cleared its indicator, so
        // follow it back to the default order rather than diverge.
        onColumnHeaderClick={({ sort }) =>
          tableState.setSortState(sort || undefined)
        }
        enableColumnReorder={true}
        enableColumnResize={true}
        enableActionButton={true}
        onActionButtonClick={handleActionButtonClick}
        manualSearch={true}
        manualSorting={true}
        manualFiltering={true}
        tableMenuOptions={{
          enabled: true,
          addButtonOptions: {
            enabled: !!onCreateClick,
            label: "New Validation",
            onClick: onCreateClick,
          },
          searchOptions: {
            enabled: true,
            placeholder: "Search Validations",
            // mfui already debounces the input 350ms before calling onSearch.
            onSearch: (term) => tableState.setSearchState(term.trim()),
          },
          exportOptions: {
            enabled: false,
          },
          tableCountOptions: {
            recordsTotalEnabled: true,
          },
          columnSelectorOptions: {
            enabled: true,
          },
        }}
      />
      {showFlyout && flyoutData && (
        <AssetValidationFlyout
          show={showFlyout}
          onClose={() => setShowFlyout(false)}
          defaultInfo={flyoutData}
          usersById={usersById}
          onNext={() => handleGetNextItem(flyoutData.uuid)}
          onPrevious={() => handleGetPrevItem(flyoutData.uuid)}
        />
      )}
    </TableWrap>
  );
};
